<template>
  <div>
    <h4 class="mb-3">Блокировка игроков</h4>

    <loading-indicator v-if="isLoading"/>

    <form v-if="isPlayersVisible" @submit.prevent="banPlayersWithConfirmation">

      <suspected-players-table :players="players" class="mb-5"/>

      <div class="form-group">
        <label for="duration-type-selector">Тип</label>
        <b-form-select
          id="duration-type-selector"
          v-model="durationType"
          :options="durationTypeOptions"
          autofocus
        />
      </div>

      <div v-if="isTemp" class="form-group">
        <label for="ban-duration">Длительность (в днях)</label>
        <input
          type="number"
          id="ban-duration"
          v-model="durationDays"
          class="form-control"
          min="1"
          required
        >
      </div>
    
      <div class="form-group">
        <label for="reason">Причина</label>
        <textarea
          v-model="reason"
          id="reason"
          class="form-control"
          rows="3"
          maxlength="255"
          required
        />
      </div>

      <b-button :to="{name: 'suspected_players'}">Отмена</b-button>
      <b-button type="submit" variant="danger">
        Заблокировать
      </b-button>
    </form>
  </div>
</template>

<script>
import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

import { BAN_TYPE_TEMP             } from '../model/player_ban.js';
import { getBanDurationTypeOptions } from '../model/player_ban.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import LoadingIndicator from './LoadingIndicator.vue';
import SuspectedPlayersTable from './SuspectedPlayersTable.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'SuspectedPlayersBanAdd',

  components: {
    LoadingIndicator,
    SuspectedPlayersTable,
  },

  props: {
    playerIds: {
      type: Array,
      required: true,
    },
  },

  data() {
    const durationTypeOptions = getBanDurationTypeOptions();

    return {
      loadingStatus: LOADING_STATUS__INIT,

      /** @type {Object[] | null} */
      players: null,

      durationTypeOptions: durationTypeOptions,
      durationType:        durationTypeOptions[0].value,

      durationDays: 10,
      reason: '',
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    isPlayersVisible() {
      if(!this.isLoadedNormal) {
        return false;
      }

      return this.players !== null;
    },

    /** @returns {boolean} */
    isTemp() {
      return this.durationType === BAN_TYPE_TEMP;
    },

    /** @returns {?number} */
    durationDaysOrNull() {
      if(this.isTemp) {
        return parseInt(this.durationDays, 10);
      }
      return null;
    },
  },

  created() {
    this.loadPlayers();
  },

  methods: {
    loadPlayers() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = `/suspected_players_info`;

      const playerIdsStr = this.playerIds.join(',');

      const requestConf = {
        params: {
          ids: playerIdsStr,
        }
      };

      HTTP.get(url, requestConf)
      .then(response => {
        /** @type {Object[] | null} */
        const data = response.data.data;
        
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.players = data;
        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    banPlayersWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const [playersBanDef, err] = this.getPlayersBanDef();
      if(err !== null) {
        notifier.showError(err, {sender: this});
        return;
      }
      if(playersBanDef === null) {
        notifier.showError('Can not ban players. playersBanDef === null.', {sender: this});
        return;
      }

      this.confirmPlayersBan(
        playersBanDef.playerIds,
        () => {
          this.banPlayers(playersBanDef);
        }
      );
    },

    /**
     * @returns {[?PlayersBanDef, ?Error]}
     */
    getPlayersBanDef() {
      const playerIds = this.playerIds;
      const durationDaysOrNull = this.durationDaysOrNull;

      const trimmedReason = this.reason.trim();
      if(trimmedReason.length === 0) {
        return [null, new Error('Причина должна быть указана.')];
      }

      const playersBanDef = new PlayersBanDef(
        playerIds,
        durationDaysOrNull,
        trimmedReason
      );

      return [playersBanDef, null];
    },

    /**
     * @param {number[]} playerIds
     * @param {ConfirmHandle} onConfirmed
     */
     confirmPlayersBan(playerIds, onConfirmed) {
      confirmAction(
        this,
        getPlayersBanConfirmationMessage(playerIds),
        'Ban',
        onConfirmed,
      );
    },

    banPlayers(playersBanDef) {
      const data = new FormData();

      const durationDays = playersBanDef.durationDays;
      if(durationDays !== null) {
        data.set('duration_days', durationDays);
      }

      const idsStr = playersBanDef.playerIds.join(',');
      data.set('ids', idsStr);

      data.set('reason', playersBanDef.reason);

      HTTP.post('/players/ban', data)
      .then(() => {
        const playerAmount = playersBanDef.playerIds.length;
        const message = getPlayersBanCompleteMessage(playerAmount);
        notifier.showSuccessAfterTransition(message);

        this.$router.push({name: 'suspected_players'});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};

class PlayersBanDef {
  /**
   * @param {number[]} playerIds
   * @param {?number}  durationDays
   * @param {string}   reason
   */
  constructor(playerIds, durationDays, reason) {
    this.playerIds    = playerIds;
    this.durationDays = durationDays;
    this.reason       = reason;
  }
}

/**
 * @param {number[]} playerIds
 * @returns {string}
 */
 function getPlayersBanConfirmationMessage(playerIds) {
  if(playerIds.length === 1) {
    const playerId = playerIds[0];
    return `Are you sure you want to ban player ${playerId} {env}?`
  }

  const amount = playerIds.length;
  return `Are you sure you want to ban ${amount} players {env}?`;
}

/**
 * @param {number} amount
 * @returns {string}
 */
function getPlayersBanCompleteMessage(amount) {
  if(amount === 1) {
    return 'Игрок заблокирован.';
  }

  return 'Игроки заблокированы.';
}
</script>
